"use client";

import {
  useAcessoMutation,
  explodeGroups,
  calculatePageAccess,
  extractTenantId,
  LoginParams,
  LoginResult,
  AuthResponse,
} from "@login";
import { useCallback } from "react";
import { usePersistUserData } from "./_zustand";
import { useUserStore } from "store";
import { signIn, getSession, signOut } from "next-auth/react";
import { Session } from "next-auth";
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import { URL } from "@/_nucleo/api/axios-base";
import axios from "axios";

interface SessionWithAuth extends Session {
  access_token?: string;
  refresh_token?: string;
  scope?: string;
  token_type?: string;
  expires_in?: number;
  hasMFA?: boolean;
}

/**
 * Hook de login maestro: autentica, busca acesso, valida, persiste tudo no Zustand e retorna resultado.
 */
export function useLoginFlow({ username }: { username: string }) {
  const acesso = useAcessoMutation(username, { enabled: false });
  const persistUserData = usePersistUserData();

  const loginAll = useCallback(
    async ({
      username,
      password,
    }: LoginParams): Promise<LoginResult | null> => {
      // Toast de início do processo
      const loadingToast = toast.loading("Iniciando autenticação...");

      try {
        // 1. Autenticação via NextAuth

        toast.loading("Verificando credenciais...", { id: loadingToast });
        const res = await signIn("credentials", {
          username,
          password,
          redirect: false,
        });
        const session = await getSession();

        if (res?.error || !session) {
          console.error("❌ [useLoginFlow] Falha na autenticação:", {
            error: res?.error,
            hasSession: !!session,
            username,
          });
          
          // ✅ CORREÇÃO: Verifica se a senha expirou via API do usuário
          try {
            const userResponse = await axios.get(`${URL.in.API_USER}/${username}`);
            if (userResponse.data.success && userResponse.data.data?.usuario?.ultimaAlteracao) {
              const { checkPasswordExpiration } = await import("@login");
              const remainingDays = checkPasswordExpiration(userResponse.data.data.usuario.ultimaAlteracao);
              if (remainingDays <= 0) {
                toast.error("🚫 Sua senha expirou. Redefina no Protheus.", {
                  id: loadingToast,
                });
                return null;
              }
            }
          } catch (apiError) {
            console.warn("⚠️ [useLoginFlow] Erro ao verificar senha via API:", apiError);
          }
          
          toast.error("Credenciais inválidas. Verifique usuário e senha.", {
            id: loadingToast,
          });
          return null;
        }

        toast.success("✅ Autenticação realizada com sucesso!", {
          id: loadingToast,
        });

        // 2. Validação da sessão

        if (!session) {
          console.error(
            "❌ [useLoginFlow] Sessão não encontrada após autenticação"
          );
          toast.error("❌ Sessão não encontrada. Tente novamente.", {
            id: loadingToast,
          });
          return {
            tenantId: "",
            authData: {
              access_token: "",
              refresh_token: "",
              scope: "",
              token_type: "",
              expires_in: 0,
              hasMFA: false,
            },
            acessoData: {
              usuario: {
                id: "",
                nome: "",
                login: "",
                status: "bloqueado",
              },
              grupos: [],
              cadastros: { condicoesPagamento: [], centrosCusto: [] },
            },
          };
        }

        const s = session as SessionWithAuth;
        const authData: AuthResponse = {
          access_token: s.access_token ?? "",
          refresh_token: s.refresh_token ?? "",
          scope: s.scope ?? "",
          token_type: s.token_type ?? "",
          expires_in: s.expires_in ?? 0,
          hasMFA: Boolean(s.hasMFA),
        };

        // 3. Busca dados de acesso

        toast.loading("Buscando dados de acesso...", { id: loadingToast });
        const acessoDataResult = await acesso.refetch();

        if (acessoDataResult.status !== "success" || !acessoDataResult.data) {
          const errorMsg =
            acessoDataResult.error?.message || "Falha ao obter dados de acesso";
          console.error("❌ [useLoginFlow] Falha ao buscar dados de acesso:", {
            status: acessoDataResult.status,
            error: acessoDataResult.error,
            hasData: !!acessoDataResult.data,
          });
          toast.error(`❌ ${errorMsg}`, { id: loadingToast });
          throw acessoDataResult.error || new Error(errorMsg);
        }

        const acessoData = acessoDataResult.data;

        toast.success("✅ Dados de acesso carregados!", { id: loadingToast });

        // 4. Validação dos dados essenciais

        toast.loading("Validando permissões...", { id: loadingToast });

        const validationWarnings = [];

        if (!acessoData.grupos?.length) {
          validationWarnings.push("Grupos de acesso");
          console.warn("⚠️ [useLoginFlow] Nenhum grupo de acesso encontrado");
        }
        if (!acessoData.cadastros?.condicoesPagamento?.length) {
          validationWarnings.push("Condições de pagamento");
          console.warn(
            "⚠️ [useLoginFlow] Nenhuma condição de pagamento encontrada"
          );
        }
        if (!acessoData.cadastros?.centrosCusto?.length) {
          validationWarnings.push("Centros de custo");
          console.warn("⚠️ [useLoginFlow] Nenhum centro de custo encontrado");
        }

        if (validationWarnings.length > 0) {
          console.warn(
            "⚠️ [useLoginFlow] Dados incompletos detectados:",
            validationWarnings
          );
          toast.error(
            `⚠️ Dados incompletos: ${validationWarnings.join(
              ", "
            )}. Algumas funcionalidades podem estar limitadas.`,
            { id: loadingToast, duration: 5000 }
          );
        } else {
        }

        // 5. Extração de filiais

        const { filiais } = explodeGroups(acessoData.grupos);

        if (!filiais.length) {
          console.error(
            "❌ [useLoginFlow] Nenhuma filial encontrada para o usuário"
          );
          toast.error(
            "❌ Nenhuma filial encontrada para seu usuário. Contate o suporte.",
            { id: loadingToast }
          );
        } else {
          toast.success(`✅ ${filiais.length} filial(is) configurada(s)`, {
            id: loadingToast,
          });
        }

        // 6. Cálculo de permissões

        const groupIds = acessoData.grupos.map((g) => g.codigo);
        const pageAccess = calculatePageAccess(groupIds);
        const tenantId = extractTenantId(acessoData.grupos);

        // 7. Persistência dos dados

        toast.loading("Salvando dados de sessão...", { id: loadingToast });
        persistUserData({
          usuarioResponse: acessoData,
          pageAccess,
          authData,
        });

        // 8. Resumo final

        const userInfo = acessoData.usuario;
        const gruposCount = acessoData.grupos.length;
        const filiaisCount = filiais.length;

        toast.success(
          `🎉 Login realizado com sucesso!\n👤 ${userInfo.nome}\n🏢 ${filiaisCount} filial(is)\n👥 ${gruposCount} grupo(s)`,
          {
            id: loadingToast,
            duration: 4000,
            style: {
              whiteSpace: "pre-line",
              textAlign: "left",
            },
          }
        );

        return { tenantId, authData, acessoData };
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Erro desconhecido";

        // Detecta tipo específico de erro para melhor UX
        let errorType = "unknown";
        let userFriendlyMessage = errorMessage;

        if (errorMessage.includes("NetworkError")) {
          errorType = "network";
          userFriendlyMessage =
            "Erro de conexão. Verifique sua internet e tente novamente.";
        } else if (
          errorMessage.includes("timeout") ||
          errorMessage.includes("Tempo limite")
        ) {
          errorType = "timeout";
          userFriendlyMessage =
            "Conexão lenta. Verifique sua internet e tente novamente.";
        } else if (
          errorMessage.includes("Servidor de autenticação indisponível")
        ) {
          errorType = "server";
          userFriendlyMessage =
            "Servidor temporariamente indisponível. Tente novamente em alguns minutos.";
        } else if (errorMessage.includes("Credenciais inválidas")) {
          errorType = "credentials";
          userFriendlyMessage =
            "Usuário ou senha incorretos. Verifique suas credenciais.";
        }

        console.error("💥 [useLoginFlow] Erro durante o processo de login:", {
          error: errorMessage,
          errorType,
          username,
          stack: error instanceof Error ? error.stack : undefined,
        });

        // Toast específico baseado no tipo de erro
        if (errorType === "network" || errorType === "timeout") {
          toast.error(`🌐 ${userFriendlyMessage}`, {
            id: loadingToast,
            duration: 6000,
            icon: "🌐",
          });
        } else if (errorType === "server") {
          toast.error(`🔧 ${userFriendlyMessage}`, {
            id: loadingToast,
            duration: 8000,
            icon: "🔧",
          });
        } else if (errorType === "credentials") {
          toast.error(`🔐 ${userFriendlyMessage}`, {
            id: loadingToast,
            duration: 4000,
            icon: "🔐",
          });
        } else {
          toast.error(`❌ ${userFriendlyMessage}`, { id: loadingToast });
        }

        throw error;
      }
    },
    [acesso, persistUserData]
  );

  return {
    loginAll,
    loading: acesso.isFetching,
    error: acesso.error,
  };
}

/**
 * Hook para logout: limpa o Zustand e redireciona para /login
 */
export function useLogout() {
  const clear = useUserStore((s) => s.clear);
  const router = useRouter();

  return () => {
    const logoutToast = toast.loading("Fazendo logout...");

    try {
      clear();

      signOut({ redirect: false })
        .then(() => {
          toast.success("✅ Logout realizado com sucesso!", {
            id: logoutToast,
          });

          router.push("/login");
        })
        .catch((error) => {
          console.error("❌ [useLogout] Erro no signOut:", {
            error: error.message,
            stack: error.stack,
          });
          toast.error(`❌ Erro no logout: ${error.message}`, {
            id: logoutToast,
          });
        });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";
      console.error("💥 [useLogout] Erro durante o logout:", {
        error: errorMessage,
        stack: error instanceof Error ? error.stack : undefined,
      });
      toast.error(`❌ Erro no logout: ${errorMessage}`, { id: logoutToast });
    }
  };
}
